// Event Capture System - Global listeners for heatmap-level tracking
import type { ClickEvent, RageClickDetection, DeadClickDetection } from '@/types/analytics';

type EventCallback = (eventData: Record<string, unknown>) => void;

class EventCaptureManager {
  private isInitialized = false;
  private callbacks: EventCallback[] = [];
  private rageClickMap: Map<string, RageClickDetection> = new Map();
  private deadClickMap: Map<string, DeadClickDetection> = new Map();
  private lastScrollDepth = 0;
  private maxScrollDepth = 0;
  private pageLoadTime = 0;
  private routeCheckHandler?: () => void;
  // Original history methods, captured once so we can restore them in
  // destroy() without leaving the patched versions hanging on the global
  // for the next page lifetime.
  private originalPushState?: typeof history.pushState;
  private originalReplaceState?: typeof history.replaceState;
  private throttledHandleScroll: EventListener | null = null;

  /**
   * Initialize global event listeners
   */
  initialize(callback: EventCallback): void {
    if (this.isInitialized) return;

    this.callbacks.push(callback);
    this.isInitialized = true;
    this.pageLoadTime = Date.now();

    // Click events
    document.addEventListener('click', this.handleClick, true);

    // Scroll events (throttled) — store ref for proper cleanup
    this.throttledHandleScroll = this.throttle(this.handleScroll, 500);
    document.addEventListener('scroll', this.throttledHandleScroll, { passive: true });

    // Form interactions
    document.addEventListener('focus', this.handleFormFocus, true);
    document.addEventListener('change', this.handleFormChange, true);
    document.addEventListener('submit', this.handleFormSubmit, true);

    // Page visibility
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    // Before unload - flush any pending data
    window.addEventListener('beforeunload', this.handleBeforeUnload);

    // Detect route changes (for SPAs)
    this.observeRouteChanges();

  }

  /**
   * Add additional callback
   */
  addCallback(callback: EventCallback): void {
    this.callbacks.push(callback);
  }

  /**
   * Remove callback
   */
  removeCallback(callback: EventCallback): void {
    this.callbacks = this.callbacks.filter(cb => cb !== callback);
  }

  /**
   * Cleanup all listeners
   */
  cleanup(): void {
    if (!this.isInitialized) return;

    document.removeEventListener('click', this.handleClick, true);
    if (this.throttledHandleScroll) {
      document.removeEventListener('scroll', this.throttledHandleScroll);
      this.throttledHandleScroll = null;
    }
    document.removeEventListener('focus', this.handleFormFocus, true);
    document.removeEventListener('change', this.handleFormChange, true);
    document.removeEventListener('submit', this.handleFormSubmit, true);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('beforeunload', this.handleBeforeUnload);

    // Clean up route observation
    if (this.routeCheckHandler) {
      window.removeEventListener('popstate', this.routeCheckHandler);
    }
    // Restore original pushState/replaceState if we patched them.
    // Without this we'd leak the patched closures across re-inits and
    // double-fire the route handler.
    if (this.originalPushState) {
      history.pushState = this.originalPushState;
      this.originalPushState = undefined;
    }
    if (this.originalReplaceState) {
      history.replaceState = this.originalReplaceState;
      this.originalReplaceState = undefined;
    }

    this.isInitialized = false;
    this.callbacks = [];
  }

  /**
   * Handle click events
   */
  private handleClick = (event: MouseEvent): void => {
    const target = event.target as HTMLElement;
    if (!target) return;

    const selector = this.generateSelector(target);
    const text = this.extractText(target);

    const clickEvent: ClickEvent = {
      element: target,
      selector,
      text,
      x: Math.round((event.clientX / window.innerWidth) * 100),
      y: Math.round((event.clientY / window.innerHeight) * 100),
      timestamp: Date.now(),
    };

    // Detect rage clicks
    this.detectRageClick(selector);

    // Detect dead clicks
    this.detectDeadClick(selector, target);

    // Emit click event
    this.emit({
      type: 'click',
      selector,
      text,
      xPosition: clickEvent.x,
      yPosition: clickEvent.y,
      timestamp: clickEvent.timestamp,
      isRageClick: (this.rageClickMap.get(selector)?.clicks ?? 0) >= 3,
    });
  };

  /**
   * Detect rage clicks (3+ clicks in 1 second on same element)
   */
  private detectRageClick(selector: string): void {
    const now = Date.now();
    const existing = this.rageClickMap.get(selector);

    if (existing && now - existing.lastClickTime < 1000) {
      existing.clicks++;
      existing.lastClickTime = now;

      if (existing.clicks >= 3) {
        this.emit({
          type: 'rage_click',
          selector,
          clicks: existing.clicks,
          timestamp: now,
        });
      }
    } else {
      this.rageClickMap.set(selector, { selector, clicks: 1, lastClickTime: now });
    }

    // Cleanup old entries
    if (this.rageClickMap.size > 100) {
      const oldestKey = Array.from(this.rageClickMap.keys())[0];
      this.rageClickMap.delete(oldestKey);
    }
  }

  /**
   * Detect dead clicks (clicks with no response in 2 seconds)
   */
  private detectDeadClick(selector: string, element: HTMLElement): void {
    const clickTime = Date.now();
    this.deadClickMap.set(selector, { selector, clickTime, responded: false });

    // Cleanup old dead click entries to prevent memory leak
    if (this.deadClickMap.size > 100) {
      const oldestKey = Array.from(this.deadClickMap.keys())[0];
      this.deadClickMap.delete(oldestKey);
    }

    let observer: MutationObserver | null = null;

    // Mark as responded if we detect changes
    try {
      observer = new MutationObserver(() => {
        const detection = this.deadClickMap.get(selector);
        if (detection) {
          detection.responded = true;
        }
        if (observer) {
          observer.disconnect();
          observer = null;
        }
      });

      // Observe only the clicked element's parent for performance
      const observeTarget = element.parentElement || document.body;
      observer.observe(observeTarget, {
        childList: true,
        subtree: false,
        attributes: true,
      });
    } catch (_error) {
      // silent — non-critical
    }

    // Check for DOM changes or navigation within 2 seconds
    window.setTimeout(() => {
      const detection = this.deadClickMap.get(selector);
      if (!detection || detection.responded) {
        if (observer) observer.disconnect();
        return;
      }

      // Simple heuristic: check if element still exists and visible
      try {
        const stillExists = document.contains(element);
        const isVisible = element.offsetParent !== null;

        if (stillExists && isVisible && !detection.responded) {
          this.emit({
            type: 'dead_click',
            selector,
            timestamp: clickTime,
          });
        }
      } catch (_error) {
        // silent — non-critical
      }

      this.deadClickMap.delete(selector);
      if (observer) {
        observer.disconnect();
        observer = null;
      }
    }, 2000);
  }

  /**
   * Handle scroll events (throttled to 500ms)
   */
  private handleScroll = (): void => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
    const scrollPercent = scrollHeight > 0 ? Math.max(0, Math.min(Math.round((scrollTop / scrollHeight) * 100), 100)) : 0;

    this.lastScrollDepth = scrollPercent;
    this.maxScrollDepth = Math.max(this.maxScrollDepth, scrollPercent);

    // Emit scroll event
    this.emit({
      type: 'scroll',
      depth: scrollPercent,
      maxDepth: this.maxScrollDepth,
      timestamp: Date.now(),
    });
  };

  /**
   * Handle form focus events
   */
  private handleFormFocus = (event: FocusEvent): void => {
    const target = event.target as HTMLElement;
    if (!this.isFormElement(target)) return;

    const selector = this.generateSelector(target);
    const fieldName = (target as HTMLInputElement).name || (target as HTMLInputElement).id || 'unknown';
    const fieldType = (target as HTMLInputElement).type || 'text';

    this.emit({
      type: 'form_focus',
      selector,
      fieldName,
      fieldType,
      timestamp: Date.now(),
    });
  };

  /**
   * Handle form change events
   */
  private handleFormChange = (event: Event): void => {
    const target = event.target as HTMLElement;
    if (!this.isFormElement(target)) return;

    const selector = this.generateSelector(target);
    const fieldName = (target as HTMLInputElement).name || (target as HTMLInputElement).id || 'unknown';
    const fieldType = (target as HTMLInputElement).type || 'text';

    // Don't capture actual values for privacy
    this.emit({
      type: 'form_change',
      selector,
      fieldName,
      fieldType,
      hasValue: !!(target as HTMLInputElement).value,
      timestamp: Date.now(),
    });
  };

  /**
   * Handle form submit events
   */
  private handleFormSubmit = (event: Event): void => {
    const target = event.target as HTMLFormElement;
    if (!target || target.tagName !== 'FORM') return;

    const selector = this.generateSelector(target);
    const formId = target.id || target.name || 'unknown';

    this.emit({
      type: 'form_submit',
      selector,
      formId,
      timestamp: Date.now(),
    });
  };

  /**
   * Handle page visibility changes
   */
  private handleVisibilityChange = (): void => {
    const isVisible = !document.hidden;

    this.emit({
      type: 'visibility_change',
      visible: isVisible,
      timestamp: Date.now(),
    });
  };

  /**
   * Handle before unload (flush pending events)
   */
  private handleBeforeUnload = (): void => {
    this.emit({
      type: 'page_unload',
      timeOnPage: Math.floor((Date.now() - this.pageLoadTime) / 1000),
      maxScrollDepth: this.maxScrollDepth,
      timestamp: Date.now(),
    });
  };

  /**
   * Observe route changes for SPAs
   */
  private observeRouteChanges(): void {
    let lastPath = window.location.pathname;

    // Use MutationObserver to detect URL changes
    const checkRouteChange = (): void => {
      const currentPath = window.location.pathname;
      if (currentPath !== lastPath) {
        const previousPath = lastPath;
        lastPath = currentPath;

        this.emit({
          type: 'route_change',
          from: previousPath,
          to: currentPath,
          timestamp: Date.now(),
        });

        // Reset page-specific tracking
        this.pageLoadTime = Date.now();
        this.maxScrollDepth = 0;
        this.lastScrollDepth = 0;
      }
    };

    // Store reference to the handler for cleanup
    this.routeCheckHandler = checkRouteChange;

    // Check on popstate (back/forward buttons) — these are handled
    // synchronously by the browser whenever history navigation happens
    // outside our control (back/forward, hashchange, location replace
    // from native API).
    window.addEventListener('popstate', this.routeCheckHandler);

    // SPA route changes via React Router → pushState/replaceState don't
    // fire popstate, so we need a hook. Previously this was a
    // setInterval(checkRouteChange, 500) that woke up 7,200 times/hour
    // just to compare strings — wasteful on mobile especially.
    //
    // Instead, monkey-patch the two history methods exactly once, fire
    // the route-change handler synchronously after the original method
    // returns, and restore the originals in destroy(). The patch is
    // invisible to React Router (the original is still called with the
    // same args) and only adds one synchronous function call per
    // navigation.
    if (typeof history.pushState === 'function' && !this.originalPushState) {
      this.originalPushState = history.pushState.bind(history);
      const originalPush = this.originalPushState;
      const handler = this.routeCheckHandler;
      history.pushState = function (
        ...args: Parameters<typeof history.pushState>
      ): void {
        originalPush(...args);
        try { handler(); } catch { /* never let our hook break navigation */ }
      };
    }
    if (typeof history.replaceState === 'function' && !this.originalReplaceState) {
      this.originalReplaceState = history.replaceState.bind(history);
      const originalReplace = this.originalReplaceState;
      const handler = this.routeCheckHandler;
      history.replaceState = function (
        ...args: Parameters<typeof history.replaceState>
      ): void {
        originalReplace(...args);
        try { handler(); } catch { /* never let our hook break navigation */ }
      };
    }
  }

  /**
   * Generate unique CSS selector for element
   */
  private generateSelector(element: HTMLElement): string {
    if (element.id) {
      return `#${element.id}`;
    }

    const path: string[] = [];
    let current: HTMLElement | null = element;

    while (current && current !== document.body) {
      let selector = current.tagName.toLowerCase();

      // Add class if available
      if (current.className && typeof current.className === 'string') {
        const classes = current.className.trim().split(/\s+/).slice(0, 2);
        if (classes.length > 0 && classes[0]) {
          selector += `.${classes.join('.')}`;
        }
      }

      // Add nth-child if needed for uniqueness
      if (current.parentElement) {
        const siblings = Array.from(current.parentElement.children);
        const index = siblings.indexOf(current);
        if (siblings.length > 1) {
          selector += `:nth-child(${index + 1})`;
        }
      }

      path.unshift(selector);
      current = current.parentElement;

      // Limit depth to 5 levels
      if (path.length >= 5) break;
    }

    return path.join(' > ');
  }

  /**
   * Extract meaningful text from element
   */
  private extractText(element: HTMLElement): string {
    // For buttons and links, get the text content
    if (element.tagName === 'BUTTON' || element.tagName === 'A') {
      return element.textContent?.trim().slice(0, 50) || '';
    }

    // For inputs, get the placeholder or label
    if (element.tagName === 'INPUT') {
      const input = element as HTMLInputElement;
      return input.placeholder || input.ariaLabel || input.name || '';
    }

    // For other elements, get first 50 chars of text
    return element.textContent?.trim().slice(0, 50) || '';
  }

  /**
   * Check if element is a form element
   */
  private isFormElement(element: HTMLElement): boolean {
    const tagName = element.tagName;
    return ['INPUT', 'TEXTAREA', 'SELECT'].includes(tagName);
  }

  /**
   * Throttle function
   */
  private throttle<T extends (...args: unknown[]) => void>(
    func: T,
    delay: number
  ): (...args: Parameters<T>) => void {
    let timeoutId: number | null = null;
    let lastExecTime = 0;

    return (...args: Parameters<T>) => {
      const now = Date.now();

      if (now - lastExecTime >= delay) {
        func(...args);
        lastExecTime = now;
      } else {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        timeoutId = window.setTimeout(() => {
          func(...args);
          lastExecTime = Date.now();
        }, delay - (now - lastExecTime));
      }
    };
  }

  /**
   * Emit event to all callbacks
   */
  private emit(eventData: Record<string, unknown>): void {
    this.callbacks.forEach(callback => {
      try {
        callback(eventData);
      } catch (_error) {
        // silent — non-critical
      }
    });
  }

  /**
   * Get current scroll depth
   */
  getScrollDepth(): number {
    return this.lastScrollDepth;
  }

  /**
   * Get max scroll depth
   */
  getMaxScrollDepth(): number {
    return this.maxScrollDepth;
  }

  /**
   * Get time on page
   */
  getTimeOnPage(): number {
    return Math.floor((Date.now() - this.pageLoadTime) / 1000);
  }
}

// Singleton instance
export const eventCapture = new EventCaptureManager();

